import NewHeader from 'components/NewHeader';
import PostContent from 'components/Blog/PostContent';
import { PageOuterContainer } from 'components/Common/CommonWrappers';
import FloatingController from 'components/Common/FloatingController';
import Footer from 'components/Footer/Footer';
import ProductContactUs from 'components/Product/ProductContactUs';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useRouter } from 'next/router';
import {
  getAllPostIds,
  getENAllPostIds,
  getENPostInformation,
  getPostInformation,
} from 'react-query/blogApi';
import styled from 'styled-components';
import bluedotMetaImg from 'public/asset/bluedot-metatag-img.png';
import { NextSeo } from 'next-seo';
import { useIncreasePostViewCount } from 'react-query/blogHooks';

export async function getStaticPaths() {
  const data = await getAllPostIds();
  const dataEn = await getENAllPostIds();

  const totalData = [];

  data && totalData.push(...data?.post_ids);
  dataEn && totalData.push(...dataEn?.post_ids);

  const paths = totalData.map(id => ({
    params: { post_id: id + '' },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
}

const injectIframe = (htmlString: string): string => {
  let originalString = htmlString;

  const youtubeMatchesRegExp =
    /(<!-- wp:embed.*?-->)(.*?)(<!-- \/wp:embed -->)/gs;

  const videoPressMatchesRegExp =
    /(<!-- wp:video[^>]*?is-provider-videopress[^>]*?\"} -->)(.*?)(<!-- \/wp:video -->)/gs;

  const videoProviderRegex = new RegExp(
    youtubeMatchesRegExp.source + '|' + videoPressMatchesRegExp.source,
    'gs'
  );

  const matches = originalString.match(videoProviderRegex);

  if (!matches) return originalString;
  matches?.forEach(match => {
    const isYoutube = match.includes('youtube');
    if (isYoutube) {
      const isShorts = match.includes('shorts');
      const shortVersionYt = match.includes('youtu.be');
      let youtubeUrl;
      let videoId;

      if (isShorts) {
        youtubeUrl = match.match(
          /(https:\/\/www\.youtube\.com\/shorts\/.{11})/gs
        );
        videoId = youtubeUrl[1].split('https://www.youtube.com/shorts/')[1];
      } else if (shortVersionYt) {
        youtubeUrl = match.match(/https:\/\/youtu\.be\/([a-zA-Z0-9_-]{11})/gs);
        const videoIdArray = youtubeUrl[1].split('/');
        videoId = videoIdArray[videoIdArray.length - 1];
      } else {
        youtubeUrl = match.match(
          /(https:\/\/www\.youtube\.com\/watch\?v=.{11})/gs
        );
        videoId = youtubeUrl[1].split('=')[1];
      }
      const iframe = `<span class="embed-youtube" style="text-align:center; display: block;"><iframe loading="lazy" class="youtube-player" width="640" height="360" src="https://www.youtube.com/embed/${videoId}?version=3&rel=1&showsearch=0&showinfo=1&iv_load_policy=1&fs=1&hl=ko-KR&autohide=2&wmode=transparent" allowfullscreen="true" style="border:0;" sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"></iframe></span>`;
      const newHtml = match.replaceAll(youtubeUrl[0], iframe);
      originalString = originalString.replace(match, newHtml);
    } else {
      const videopressUrl = match.match(/https:\/\/videopress\.com\/v\/[^<]+/);

      const iframe = `<div class="padding-wrapper"></div><iframe title="VideoPress Video Player" class="videopress-iframe" aria-label="VideoPress Video Player" width="100%"  src=${videopressUrl} frameborder="0" allowfullscreen data-resize-to-parent="true" allow="clipboard-write"></iframe>`;
      const newHtml = match.replaceAll(videopressUrl[0], iframe);
      originalString = originalString.replace(match, newHtml);
    }
  });

  return originalString;
};

export async function getStaticProps({ params }: GetStaticPropsContext) {
  const postId = params.post_id;
  let notFound: boolean = false;
  const data = await getPostInformation(+postId as number);
  const dataEn = await getENPostInformation(+postId as number);

  // @ts-ignore;
  const totalData = data === 'no post found' ? dataEn : data;
  // @ts-ignore;
  notFound = totalData === 'no post found' ? true : false;

  if (!notFound) {
    // extract
    let html = totalData.content;

    totalData.content = injectIframe(html);
  }

  return {
    props: { post: totalData },
    revalidate: 300,
    notFound,
  };
}

const Post = ({ post }: InferGetStaticPropsType<typeof getStaticProps>) => {
  console.log(post);
  const { locale } = useRouter();

  useIncreasePostViewCount(post.id);

  return (
    <PostContainer>
      <NextSeo
        title={`${post.title} | ${post.category} - BLUEDOT Blog`}
        description={`${post.excerpt || post.title + post.category}`}
        canonical={`https://blue-dot.io/ko/blog/post/${post.id}`}
        openGraph={{
          url: `https://blue-dot.io/ko/blog/post/${post.id}`,
          title: `${post.title} | ${post.category} - BLUEDOT Blog`,
          description: `${post.excerpt || post.title + post.category}`,
          images: [
            {
              url: post.thumbnail || bluedotMetaImg.src,
              alt: 'BLUEDOT INC.',
              type: 'image/png',
            },
          ],
          siteName: 'BLUEDOT',
        }}
        twitter={{
          handle: '@handle',
          site: '@site',
          cardType: 'summary_large_image',
        }}
      />

      <NewHeader isScrolled />
      <div className="io-target" />
      <PostContent post={post} />
      <ProductContactUs locale={locale} />
      <Footer />

      <FloatingController isVisible={false} />
    </PostContainer>
  );
};

export default Post;

const PostContainer = styled(PageOuterContainer)`
  .io-target {
    height: 60px;
  }
  @media screen and (max-width: 904px) and (min-width: 600px) {
    .io-target {
      height: 52px;
    }
  }
  @media screen and (max-width: 599px) {
    .io-target {
      height: 44px;
    }
  }
`;
